import type { CashFlowRow } from "@/hooks/finance/cash-flow/useCashFlow";
import type { MatchRule } from "../financial-report/mapping";
import {
  PNL_PURCHASES_LINE,
  PNL_VARIABLE_LINES,
  PNL_SGA_SECTION,
  PNL_SGA_EXTRA_LINES,
} from "./mapping";

const norm = (s: string) => s.trim().toLowerCase();

function indexOperatingGroups(rows: CashFlowRow[]) {
  const byGroup = new Map<
    string,
    { groupRow: CashFlowRow; positions: Map<string, CashFlowRow> }
  >();
  for (const r of rows) {
    if (!r.key.startsWith("op-exp-")) continue;
    const raw = r.label.replace(/^\(/, "").replace(/\)$/, "");
    const positions = new Map<string, CashFlowRow>();
    for (const child of r.children ?? []) positions.set(norm(child.label), child);
    byGroup.set(norm(raw), { groupRow: r, positions });
  }
  return byGroup;
}

function emptyValues(periodKeys: string[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const k of periodKeys) m.set(k, 0);
  return m;
}
const addInto = (t: Map<string, number>, s?: Map<string, number>) => {
  if (!s) return;
  for (const [k, v] of s.entries()) t.set(k, (t.get(k) ?? 0) + (v ?? 0));
};
const subInto = (t: Map<string, number>, s?: Map<string, number>) => {
  if (!s) return;
  for (const [k, v] of s.entries()) t.set(k, (t.get(k) ?? 0) - (v ?? 0));
};

function aggregate(
  matches: MatchRule[],
  index: ReturnType<typeof indexOperatingGroups>,
  periodKeys: string[],
  consumed: Set<string>
): Map<string, number> {
  const out = emptyValues(periodKeys);
  for (const m of matches) {
    const entry = index.get(norm(m.group));
    if (!entry) continue;
    if (!("positions" in m) && !("excludePositions" in m)) {
      addInto(out, entry.groupRow.values);
      consumed.add(`group:${norm(m.group)}`);
      for (const p of entry.positions.keys()) consumed.add(`pos:${norm(m.group)}:${p}`);
      continue;
    }
    if ("positions" in m) {
      for (const posLabel of m.positions) {
        const pos = entry.positions.get(norm(posLabel));
        if (pos) {
          addInto(out, pos.values);
          consumed.add(`pos:${norm(m.group)}:${norm(posLabel)}`);
        }
      }
      continue;
    }
    if ("excludePositions" in m) {
      addInto(out, entry.groupRow.values);
      consumed.add(`group:${norm(m.group)}`);
      for (const [pKey] of entry.positions.entries()) {
        consumed.add(`pos:${norm(m.group)}:${pKey}`);
      }
      for (const posLabel of m.excludePositions) {
        const pos = entry.positions.get(norm(posLabel));
        if (pos) subInto(out, pos.values);
      }
    }
  }
  return out;
}

/**
 * Compute a percentage row from numerator/denominator maps.
 * Returned values are 0-1 ratios; the table formats with the % suffix.
 */
function ratioRow(
  num: Map<string, number>,
  den: Map<string, number>,
  periodKeys: string[]
): Map<string, number> {
  const out = new Map<string, number>();
  for (const k of periodKeys) {
    const d = den.get(k) || 0;
    out.set(k, d === 0 ? 0 : (num.get(k) || 0) / d);
  }
  return out;
}

export type PnlBuild = {
  rows: CashFlowRow[];
  unmappedRows: CashFlowRow[];
};

export function buildPnlRows(
  sourceRows: CashFlowRow[],
  periodKeys: string[]
): PnlBuild {
  const opIndex = indexOperatingGroups(sourceRows);
  const consumed = new Set<string>();

  const findByKey = (k: string) => sourceRows.find((r) => r.key === k);
  const totalSales = findByKey("total-sales");
  const chipchipSales = findByKey("chipchip-sales");
  const chipchipFees = findByKey("chipchip-fees");
  const b2bSales = findByKey("b2b-sales");
  const dedicatedPurchases = findByKey("pnl-purchases-dedicated");

  const rows: CashFlowRow[] = [];

  // Revenue section
  rows.push({
    key: "pnl-rev-header",
    label: "REVENUE",
    isHeader: true,
    values: emptyValues(periodKeys),
  });
  if (chipchipSales)
    rows.push({ ...chipchipSales, key: "pnl-rev-chipchip", label: "ChipChip", indent: 1 });
  if (chipchipFees)
    rows.push({ ...chipchipFees, key: "pnl-rev-fees", label: "ChipChip (Delivery & Packaging Fees)", indent: 1 });
  if (b2bSales)
    rows.push({ ...b2bSales, key: "pnl-rev-b2b", label: "B2B", indent: 1 });

  const revenue = totalSales?.values ?? emptyValues(periodKeys);
  rows.push({
    key: "pnl-total-revenue",
    label: "Total Revenue",
    isTotal: true,
    values: revenue,
  });

  // Mark the generic purchase groups as consumed for unmapped reporting, but
  // use the hook's dedicated bank-rate series for Purchases and CP1.
  const groupedPurchases = aggregate(PNL_PURCHASES_LINE.matches, opIndex, periodKeys, consumed);
  const purchases = dedicatedPurchases?.values ?? groupedPurchases;

  // CP1 rendered ABOVE the COGS header
  const cp1 = emptyValues(periodKeys);
  addInto(cp1, revenue);
  subInto(cp1, purchases);
  rows.push({
    key: "pnl-cp1",
    label: "Contribution Profit 1 (Revenue − Purchases)",
    isTotal: true,
    values: cp1,
  });
  rows.push({
    key: "pnl-cp1-margin",
    label: "  CP1 Margin %",
    indent: 1,
    values: ratioRow(cp1, revenue, periodKeys),
    categoryId: "__pct__",
  } as CashFlowRow);

  // Cost of Revenue section
  rows.push({
    key: "pnl-cogs-header",
    label: "Operating Outflows - Cost of Good Sold",
    isHeader: true,
    values: emptyValues(periodKeys),
  });
  rows.push({
    key: "pnl-line-purchases",
    label: PNL_PURCHASES_LINE.label,
    indent: 1,
    values: purchases,
  });

  // Variable service costs
  const variableTotal = emptyValues(periodKeys);
  for (const line of PNL_VARIABLE_LINES) {
    const v = aggregate(line.matches, opIndex, periodKeys, consumed);
    addInto(variableTotal, v);
    rows.push({
      key: `pnl-var-${line.label}`,
      label: line.label,
      indent: 1,
      values: v,
    });
  }

  const cp2 = emptyValues(periodKeys);
  addInto(cp2, cp1);
  subInto(cp2, variableTotal);

  // Gross Profit = CP2 (shown first)
  rows.push({
    key: "pnl-gross-profit",
    label: "Gross Profit",
    isTotal: true,
    values: cp2,
  });

  // CP2 rendered BELOW Gross Profit, ABOVE SG&A
  rows.push({
    key: "pnl-cp2",
    label: "Contribution Profit 2 (CP1 − Variable Costs)",
    isTotal: true,
    values: cp2,
  });
  rows.push({
    key: "pnl-cp2-margin",
    label: "  CP2 Margin %",
    indent: 1,
    values: ratioRow(cp2, revenue, periodKeys),
    categoryId: "__pct__",
  } as CashFlowRow);


  // Operating Expenses (SG&A)
  rows.push({
    key: "pnl-sga-header",
    label: "OPERATING EXPENSES (SG&A)",
    isHeader: true,
    values: emptyValues(periodKeys),
  });

  const sgaTotal = emptyValues(periodKeys);
  for (const line of PNL_SGA_SECTION.lines) {
    const v = aggregate(line.matches, opIndex, periodKeys, consumed);
    addInto(sgaTotal, v);
    rows.push({
      key: `pnl-sga-${line.label}`,
      label: line.label,
      indent: 1,
      values: v,
    });
  }

  // P&L-only extras (Depreciation, etc.)
  for (const line of PNL_SGA_EXTRA_LINES) {
    const v = aggregate(line.matches, opIndex, periodKeys, consumed);
    addInto(sgaTotal, v);
    rows.push({
      key: `pnl-sga-extra-${line.label}`,
      label: line.label,
      indent: 1,
      values: v,
    });
  }

  // Unmapped detection
  const unmappedRows: CashFlowRow[] = [];
  const unmappedTotal = emptyValues(periodKeys);
  for (const [gKey, entry] of opIndex.entries()) {
    if (consumed.has(`group:${gKey}`)) continue;
    if (entry.positions.size === 0) {
      unmappedRows.push({
        key: `pnl-unmapped-g-${gKey}`,
        label: `(${entry.groupRow.label.replace(/^\(|\)$/g, "")})`,
        indent: 1,
        values: entry.groupRow.values,
      });
      addInto(unmappedTotal, entry.groupRow.values);
      continue;
    }
    for (const [pKey, pos] of entry.positions.entries()) {
      if (consumed.has(`pos:${gKey}:${pKey}`)) continue;
      unmappedRows.push({
        key: `pnl-unmapped-${gKey}-${pKey}`,
        label: `${entry.groupRow.label.replace(/^\(|\)$/g, "")} › ${pos.label}`,
        indent: 1,
        values: pos.values,
      });
      addInto(unmappedTotal, pos.values);
    }
  }

  const opexWithUnmapped = emptyValues(periodKeys);
  addInto(opexWithUnmapped, sgaTotal);
  addInto(opexWithUnmapped, unmappedTotal);

  rows.push({
    key: "pnl-total-opex",
    label: "Total Operating Expenses",
    isTotal: true,
    values: opexWithUnmapped,
  });

  // EBIT
  const ebit = emptyValues(periodKeys);
  addInto(ebit, cp2);
  subInto(ebit, opexWithUnmapped);
  rows.push({
    key: "pnl-ebit",
    label: "Operating Profit (EBIT)",
    isTotal: true,
    values: ebit,
  });
  rows.push({
    key: "pnl-ebit-margin",
    label: "  EBIT Margin %",
    indent: 1,
    values: ratioRow(ebit, revenue, periodKeys),
    categoryId: "__pct__",
  } as CashFlowRow);

  return { rows, unmappedRows };
}
